import type { NotificationOptions } from "@rio-cloud/rio-uikit/Notification"

/**
 * Supported notification variants for {@link EMBED_SHOW_NOTIFICATION}.
 */
export type EmbedNotificationVariant = "default" | "info" | "success" | "warning" | "error"

/**
 * Payload of {@link EMBED_SHOW_NOTIFICATION}.
 *
 * The payload is intentionally limited to structured-clone-safe values so that embeds can send it via
 * `postMessage` without depending on React internals.
 *
 * Supported features:
 * - explicit notification `variant`
 * - textual `message`
 * - optional textual `title`
 * - optional `timeout`
 * - optional `closeButton`
 * - optional `closeOnClick`
 * - optional `hideProgressBar`
 *
 * Non-goals:
 * - callbacks
 * - React elements / arbitrary `ReactNode`
 * - notification update or dismiss handles
 *
 * Example payload:
 * ```ts
 * {
 *   variant: 'success',
 *   message: 'Document has been created.',
 *   title: 'Export finished',
 *   timeout: 5000,
 *   closeButton: true,
 * }
 * ```
 */
export type EmbedShowNotificationPayload = {
    variant: EmbedNotificationVariant;
    message: string;
    title?: string;
} & Pick<NotificationOptions, "timeout" | "closeButton" | "closeOnClick" | "hideProgressBar">

/**
 * Sent when an embed wants the host to show a UIKit notification.
 *
 * The payload is an {@link EmbedShowNotificationPayload}.
 */
export const EMBED_SHOW_NOTIFICATION = "rio.embed.showNotification"
